import { TREATMENTS, CLINIC_INFO } from '../data';
import { Clock, Info, Calendar, Phone, CreditCard, Banknote, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';

export default function Pricing() {
  useEffect(() => {
    document.title = "CDH Sports- og Wellness Massage - Priser & Åbningstider";
  }, []);

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative h-[70vh] md:h-[75vh] lg:h-[80vh] flex items-center overflow-hidden pt-32 md:pt-40 lg:pt-48">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/767bb60f-8ec8-4a4c-940e-ed49b89257a3/125d8ef7-2189-4bb7-a52a-ada72dea3de8.jpg"
            alt="Priser og Åbningstider"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-blue-900/60 z-10" />
        </div>

        <div className="container mx-auto px-4 relative z-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-[1.1]">
              Priser & Åbningstider
            </h1>
            <p className="text-xs sm:text-sm md:text-base text-white/90 mb-10 leading-relaxed font-light">
              Gennemsigtige priser og fleksible åbningstider. Find den tid der passer dig bedst og book din næste behandling i dag.
            </p>
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
              <a 
                href="#priser" 
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3.5 lg:px-8 lg:py-4 rounded-full text-base lg:text-lg font-bold transition-all shadow-lg flex items-center justify-center"
              >
                Se priser
              </a>
              <Link 
                to="/kontakt" 
                className="bg-white/10 hover:bg-white/20 backdrop-blur-md text-white border border-white/30 px-6 py-3.5 lg:px-8 lg:py-4 rounded-full text-base lg:text-lg font-bold transition-all flex items-center justify-center"
              >
                Kontakt mig
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Grid */}
      <section id="priser" className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Prices */}
            <div className="space-y-12">
              <h2 className="text-3xl font-bold text-blue-900 flex items-center">
                <span className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center mr-4">
                  <CreditCard className="w-6 h-6 text-blue-600" />
                </span>
                Priser
              </h2>
              
              <div className="space-y-8">
                {TREATMENTS.filter(t => t.pricing.some(p => p.price !== "Varierer" && p.price !== "Se priser")).map((treatment) => (
                  <div key={treatment.id} className="bg-blue-50/50 rounded-3xl p-8 border border-blue-50">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 pb-4 border-b border-blue-100">
                      <h3 className="text-xl font-bold text-blue-900">{treatment.title}</h3>
                    </div>
                    <div className="space-y-6">
                      {treatment.pricing.map((p, i) => (
                        <div key={i} className="flex justify-between items-center py-3 border-b border-blue-100/50 last:border-0">
                          <span className="flex items-center text-gray-600 text-xs sm:text-sm">
                            <Clock className="w-4 h-4 mr-2 text-emerald-500" />
                            {p.duration}
                          </span>
                          <div className="flex items-center gap-4">
                            <span className="font-bold text-blue-900 text-base sm:text-lg">{p.price}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-8 bg-amber-50 rounded-3xl border border-amber-100 flex items-start space-x-4">
                <Info className="w-6 h-6 text-amber-600 shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-amber-900 mb-2">Vigtig information</h4>
                  <ul className="text-xs sm:text-sm text-amber-800 space-y-2">
                    <li>• Alle priser er inkl. moms.</li>
                    <li>• Den angivne tid er inkl. af- og påklædning.</li>
                    <li className="flex items-center">
                      <Banknote className="w-4 h-4 mr-1" />
                      • Betaling sker kontant eller via MobilePay.
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Opening Hours & Contact */}
            <div className="space-y-12">
              <div className="bg-blue-900 rounded-[3rem] p-12 text-white shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/20 rounded-full blur-2xl" />
                <h2 className="text-3xl font-bold mb-10 flex items-center">
                  <span className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center mr-4">
                    <Clock className="w-6 h-6 text-white" />
                  </span>
                  Åbningstider
                </h2>
                <div className="space-y-6 mb-12">
                  {CLINIC_INFO.openingHours.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center py-4 border-b border-white/10 last:border-0">
                      <span className="text-blue-200 text-xs sm:text-sm font-medium">{item.day}</span>
                      <span className="font-bold text-white text-xs sm:text-sm">{item.hours}</span>
                    </div>
                  ))}
                </div>
                <Link
                  to="/bestil-tid"
                  className="block w-full text-center bg-emerald-500 hover:bg-emerald-600 text-white py-4 lg:py-5 rounded-2xl font-bold text-lg lg:text-xl shadow-lg transition-all"
                >
                  <Calendar className="w-6 h-6 inline-block mr-2" />
                  Bestil tid nu
                </Link>
                <div className="mt-8 text-center text-blue-200 text-sm">
                  <p>Har du spørgsmål til tiderne?</p>
                  <a href={`tel:${CLINIC_INFO.phone}`} className="text-white font-bold hover:text-emerald-400 transition-colors">
                    Ring til mig på {CLINIC_INFO.phone}
                  </a>
                </div>
              </div>

              {/* Booking Help */}
              <div className="bg-white rounded-[3rem] p-10 border border-blue-100 shadow-sm">
                <h3 className="text-2xl font-bold text-blue-900 mb-6">Booking info</h3>
                <p className="text-gray-600 text-sm sm:text-base mb-8 leading-relaxed">
                  Det er nemt og hurtigt at booke tid via mit online system. Skulle du have specielle ønsker eller brug for en tid uden for de normale åbningstider, så tøv ikke med at ringe.
                </p>
                <div className="flex items-center space-x-6">
                  <div className="bg-emerald-100 p-4 rounded-full">
                    <Phone className="w-8 h-8 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500 font-medium">Spørgsmål?</p>
                    <p className="text-xl font-bold text-blue-900">{CLINIC_INFO.phone}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
